import React from 'react';
import { defaultTo, get } from 'lodash';
import { CustomSearch, errorNotificationBuilder, useFeatureToggle, useNotification } from '@css-xp/base';
import { trackSearchEvent } from '@atlassian/temp-analytics';
import { abgGraphqlClient as graphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { usePartnerProductCatalogueContext } from '../contexts/product-catalogue-context';
import { fetchNativeProductCatalogueWithOfferingAndPricingPlans } from '@css-xp/ppc-api';
import { HEADER } from '../constants/header';
import './products-search.scss';
import {
  CATALOGUE_CONSTANTS,
  CATALOG_EXCLUDE_PRODUCTS,
  ENABLE_DESIGN_ITERATION_1,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import type { FilterOption } from '@css-xp/graphtypes/src/generated-types-new';
import { usePartnerCatalogueContext } from '../../..';

interface FormData {
  searchTerm: string;
}

export const constructQuerySearchParams = (cursor?: string, hasNextPage?: boolean, searchText?: string) => {
  return {
    variables: {
      pageParams: {
        // https://atlassian.slack.com/archives/C0355DZ6QRK/p1679892102790129
        // Because of the hard coding of initial products we have decided to make it 12
        size: CATALOGUE_CONSTANTS.DEFAULT_ROWS_PER_PAGE_FOR_CATALOGUE,
        cursor: cursor,
      },
      searchText: searchText ?? '',
    },
    hasNextPage: hasNextPage,
  };
};

export const ProductsSearch = ({
  filterOptions,
  hideCatalogExcludedProducts = false,
}: {
  filterOptions?: FilterOption[];
  hideCatalogExcludedProducts?: boolean;
}) => {
  const [isEnabled] = useFeatureToggle();
  const { setSearchResults, setIsTableLoading, setQuerySearch, isTableLoading } = usePartnerProductCatalogueContext();
  const { pricingPlanCycle } = usePartnerCatalogueContext();
  const { notificationSent, sendErrorNotification, setNotificationSent } = useNotification();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isAppificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APPIFICATION);
  const isAddonPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2);

  const placeholderText = React.useMemo(() => {
    if (isAppificationEnabled) {
      return HEADER.SEARCH_APP_PLACEHOLDER;
    }
    if (isAddonPhase2Enabled) {
      return HEADER.SEARCH_PLACEHOLDER_WITH_ADDONS;
    }
    return HEADER.SEARCH_PLACEHOLDER;
  }, [isAddonPhase2Enabled, isAppificationEnabled]);

  const handleSearchSubmit = async (formData: FormData) => {
    let response;
    const input = formData && formData.searchTerm;

    // Add the loading state for the products table
    setIsTableLoading(true);
    trackSearchEvent();

    try {
      response = await graphqlClient(ProxyGraphClients.SXP).query({
        query: fetchNativeProductCatalogueWithOfferingAndPricingPlans,
        variables: {
          pageParams: {
            size: CATALOGUE_CONSTANTS.DEFAULT_ROWS_PER_PAGE_FOR_CATALOGUE,
          },
          ...(input ? { searchText: input } : {}),
          pricingPlanFilterOptions: [
            {
              key: 'currency',
              value: 'USD',
            },
            {
              key: 'billingFrequency',
              value: pricingPlanCycle,
            },
          ],
          filterOptions,
          includeExperimentalProducts: isEnabled(STATSIG_FEATURE_GATES.ENABLE_EXPERIMENTAL_PRODUCTS_IN_CATALOG),
          isAddOnsSearchEnabled: isAddonPhase2Enabled,
        },
        fetchPolicy: 'cache-first',
      });
    } catch (err) {
      // Remove the loading state for the products table
      setIsTableLoading(false);
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .withId('product-search-failure')
          .withMessage(defaultTo(get(err, 'message'), ''))
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
      return;
    }

    // Remove the loading state for the products table
    setIsTableLoading(false);

    // Note: Not using the optional chaining (?.) to improve branch coverage in tests
    const searchResults = (() => {
      const products = defaultTo(get(response, 'data.nativeCatalog.nodes'), []);
      if (!hideCatalogExcludedProducts) return products;
      return products.filter((product: any) => product && !CATALOG_EXCLUDE_PRODUCTS.includes(product?.id));
    })();
    const endCursor = defaultTo(get(response, 'data.nativeCatalog.pageInfo.endCursor'), false);
    const hasNextPage = defaultTo(get(response, 'data.nativeCatalog.pageInfo.hasNextPage'), undefined);
    // Storing all the page params into product catalog context since it is used in other page as well
    setQuerySearch(constructQuerySearchParams(endCursor, hasNextPage, input));
    setSearchResults(searchResults);
  };

  return (
    <div className="ppc-product-table-search">
      <CustomSearch
        onSearch={handleSearchSubmit}
        placeholder={placeholderText}
        isClearable
        isLoading={isDesignIteration1Enabled && isTableLoading}
      />
    </div>
  );
};
